import React, {useMemo, useState} from 'react';
import {Detail, Modal, Tabs} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import UpdateFeatureFlag from '../../../../mutations/mission_control/feature_flag_mutation';
import {FeatureFlagAssignmentRow, FeatureFlagDetailData} from '../featureFlagDetailData';
import {
	buildAllUsersInput,
	buildBulkAddInput,
	buildNamedUsersInput,
	buildRemoveAccessInput,
	clampTargetPercent,
	computeFillCompanyIds,
	FeatureFlagAccessInput,
} from './featureFlagRollout';
import {
	buildRolloutCandidates,
	companyOptionsForPicker,
	RolloutDirectory,
	RolloutPersonOption,
} from './featureFlagRolloutDirectory';
import {RolloutPercentageTab} from './RolloutPercentageTab';
import {RolloutPreviewTable} from './RolloutPreviewTable';
import {RolloutSpecificTab} from './RolloutSpecificTab';

const PERCENTAGE_TAB = 'percentage';
const SPECIFIC_TAB = 'specific';

export interface FeatureFlagRolloutModalProps {
	detail: FeatureFlagDetailData;
	directory: RolloutDirectory;
	eligibleCompanyIds: Set<string>;
	/** Encoded company id when editing one company's access; null when adding. */
	editingCompanyId: string | null;
	onClose: () => void;
}

export function FeatureFlagRolloutModal({
	detail,
	directory,
	eligibleCompanyIds,
	editingCompanyId,
	onClose,
}: FeatureFlagRolloutModalProps): JSX.Element {
	const editingRow = useMemo(
		() => (editingCompanyId == null ? null : detail.rows.find(row => row.companyId === editingCompanyId) ?? null),
		[detail.rows, editingCompanyId]
	);

	// Editing is always about one company's own access, so the percentage tab has nothing to offer and
	// the strip collapses to the one tab that does.
	const isEditing = editingRow != null;
	const [tab, setTab] = useState<string>(isEditing ? SPECIFIC_TAB : PERCENTAGE_TAB);

	const [targetPercent, setTargetPercent] = useState(detail.rolloutPercent);
	// Who the operator has unchecked in the preview. Held rather than the selected set, so a company
	// that appears when the target is raised is included without being added here first.
	const [heldBackCompanyIds, setHeldBackCompanyIds] = useState<Set<string>>(new Set());

	const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(editingCompanyId);

	// The company currently authoritative for the specific tab — the one being edited, or, while
	// adding, whichever company the picker has selected. Equals `editingRow` when editing, since the
	// company field is locked there and `selectedCompanyId` never changes.
	const selectedCompanyRow = useMemo(
		() => (selectedCompanyId == null ? null : detail.rows.find(row => row.companyId === selectedCompanyId) ?? null),
		[detail.rows, selectedCompanyId]
	);

	const [allUsers, setAllUsers] = useState(selectedCompanyRow?.allUsers ?? false);

	/**
	 * Companies a percentage fill will not offer, because they are already listed on the flag.
	 *
	 * This is every company in the assignment table, which includes companies holding the flag only
	 * through named users — so a percentage rollout never converts one of those into an all-users grant.
	 * That is deliberate: such a company was configured by hand, and silently widening it to everyone as
	 * a side effect of raising a percentage would be a surprise. Widen it from its own row instead.
	 */
	const grantedCompanyIds = useMemo(() => new Set(detail.rows.map(row => row.companyId)), [detail.rows]);

	// How many eligible companies a percentage rollout cannot reach no matter the target, because they
	// hold the flag through named users only and `grantedCompanyIds` excludes them from every fill. The
	// percentage tab surfaces this so a 100% target that cannot actually land at 100% is not a silent
	// surprise once saved.
	const namedUsersOnlyEligibleCount = useMemo(
		() => detail.rows.filter(row => !row.allUsers && eligibleCompanyIds.has(row.companyId)).length,
		[detail.rows, eligibleCompanyIds]
	);

	const personsForCompany = (companyId: string | null): RolloutPersonOption[] =>
		companyId == null ? [] : directory.personsByCompanyId.get(companyId) ?? [];

	// The person ids a row's existing grant seeds the specific tab with, identified by the person id the
	// backend recorded on each grant — not by matching rendered labels, which would silently drop
	// anyone missing from the rollout directory (a deactivated, virtual, or client person, or the
	// restricted population a Sage support viewer sees) the next time this company's access is saved.
	// Empty for an all-users row: there is no named-user list to seed from.
	const seedPersonIdsFromRow = (row: FeatureFlagAssignmentRow | null): string[] => {
		if (row == null || row.allUsers) {
			return [];
		}
		return (row.userPersonIds ?? []).filter((id): id is string => id != null);
	};

	// Seeded from `selectedCompanyRow` at mount — either the company being edited, or, when adding, a
	// company the picker already had selected (there is none at mount for a fresh add). Picking a
	// DIFFERENT company while adding is handled in `handleSelectCompany` below, not here: this
	// initializer only runs once.
	const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>(() => seedPersonIdsFromRow(selectedCompanyRow));

	// Falls back to the row's own label for a granted person the rollout directory does not contain, so
	// working on a company's access never silently drops someone from what is displayed or from what a
	// save preserves — only from what a NEW pick can add, since the directory is also where new users
	// are chosen from.
	const rowPersonLabels = useMemo(() => {
		const labels = new Map<string, string>();
		if (selectedCompanyRow == null) {
			return labels;
		}
		(selectedCompanyRow.userPersonIds ?? []).forEach((id, index) => {
			if (id != null) {
				labels.set(id, selectedCompanyRow.users[index]);
			}
		});
		return labels;
	}, [selectedCompanyRow]);

	const fillCompanyIds = useMemo(
		() =>
			computeFillCompanyIds({
				flagKey: detail.key,
				eligibleCompanyIds,
				grantedCompanyIds,
				targetPercent,
				currentEligibleGranted: detail.eligibleGranted,
			}),
		[detail.eligibleGranted, detail.key, eligibleCompanyIds, grantedCompanyIds, targetPercent]
	);

	const candidates = useMemo(() => buildRolloutCandidates(fillCompanyIds, directory), [directory, fillCompanyIds]);
	const companiesToAdd = fillCompanyIds.filter(companyId => !heldBackCompanyIds.has(companyId));

	const companyOptions = useMemo(
		() => companyOptionsForPicker(directory, eligibleCompanyIds),
		[directory, eligibleCompanyIds]
	);

	const availablePersons = personsForCompany(selectedCompanyId);
	const selectedPersons = selectedPersonIds
		.map((personId): RolloutPersonOption | undefined => {
			const fromDirectory = availablePersons.find(person => person.id === personId);
			if (fromDirectory) {
				return fromDirectory;
			}
			const fallbackLabel = rowPersonLabels.get(personId);
			return fallbackLabel != null ? {id: personId, name: fallbackLabel} : undefined;
		})
		.filter((person): person is RolloutPersonOption => person != null);
	const personOptions = availablePersons.filter(person => !selectedPersonIds.includes(person.id));

	const handleToggleCompany = (companyId: string): void => {
		setHeldBackCompanyIds(held => {
			const next = new Set(held);
			if (next.has(companyId)) {
				next.delete(companyId);
			} else {
				next.add(companyId);
			}
			return next;
		});
	};

	const handleSelectCompany = (companyId: string | null): void => {
		// Only a move to a DIFFERENT company re-seeds the selection below, because re-seeding discards
		// every user added since the company was picked.
		//
		// A null report is the design-system ComboBox's own text-cleared echo — it fires whenever the
		// input text is cleared, including by backspacing into an already-picked company's name to
		// re-type it, not only on a deliberate change. There is no "deselect the company" action in
		// this flow, so treating null as a real change would silently wipe an already-accumulated user
		// list the moment the operator clicks back into the field. `featureFlagRows.ts`'s
		// `resolveTeamFilterChange` documents the same ComboBox behavior for the team filter. The
		// picker is given `selectedCompanyId` as its controlled value, so ignoring the echo leaves the
		// field showing the company that is still selected rather than going blank.
		//
		// Re-picking the company that is already selected is likewise not a change of company — it is
		// how an operator confirms the name they just backspaced into — so it must not re-seed either.
		if (companyId == null || companyId === selectedCompanyId) {
			return;
		}
		setSelectedCompanyId(companyId);
		// Seeded from the picked company's own existing grant, not cleared to empty: a company already
		// on the flag has to start from what it already has, or saving with no further changes would
		// replace its grant with nothing — silently downgrading an all-users company, or wiping a
		// named-users company, the moment the operator picks it and presses Save.
		const pickedRow = detail.rows.find(row => row.companyId === companyId) ?? null;
		setSelectedPersonIds(seedPersonIdsFromRow(pickedRow));
		setAllUsers(pickedRow?.allUsers ?? false);
	};

	/**
	 * Whether the specific tab currently describes exactly the access the selected company already has.
	 *
	 * Re-sending an unchanged grant is not a harmless no-op: the server replaces a company's access by
	 * deleting its rows and inserting new ones, so the row's "Added" date would jump to today and the
	 * record of when the company actually received the flag would be lost. Since picking a company on
	 * this tab seeds the selection from that company's existing grant, "unchanged" is the state the
	 * operator sees immediately after picking one — or, in edit mode, the state the modal opens in.
	 *
	 * False for a company with no grant yet: there is no "Added" date to protect and nothing the save
	 * would be repeating.
	 */
	const isUnchangedFromRow = ((): boolean => {
		if (selectedCompanyRow == null || allUsers !== selectedCompanyRow.allUsers) {
			return false;
		}
		if (allUsers) {
			return true;
		}
		const seeded = new Set(seedPersonIdsFromRow(selectedCompanyRow));
		return seeded.size === selectedPersonIds.length && selectedPersonIds.every(personId => seeded.has(personId));
	})();

	/**
	 * What this modal would send, or null when there is nothing valid to send.
	 *
	 * A specific-company save with no users chosen is deliberately not sendable: an empty person list
	 * would reach the server as a revocation, which is not what the operator asked for. Removing access
	 * is its own control.
	 */
	const pendingInput = ((): FeatureFlagAccessInput | null => {
		if (tab === SPECIFIC_TAB) {
			if (selectedCompanyId == null || isUnchangedFromRow) {
				return null;
			}
			if (allUsers) {
				return buildAllUsersInput(detail.key, selectedCompanyId);
			}
			return selectedPersonIds.length === 0
				? null
				: buildNamedUsersInput(detail.key, selectedCompanyId, selectedPersonIds);
		}
		return companiesToAdd.length === 0 ? null : buildBulkAddInput(detail.key, companiesToAdd);
	})();

	const [isSaving, setIsSaving] = useState(false);

	const commit = (input: FeatureFlagAccessInput): void => {
		if (isSaving) {
			return;
		}
		setIsSaving(true);
		Util.CommitMutation(
			UpdateFeatureFlag,
			input,
			() => onClose(),
			true,
			null,
			(transaction, onFailure) => {
				// onFailure returns true when it has scheduled a transient-network retry instead of
				// showing an error, in which case the save is still in flight and this modal stays put.
				const retrying = onFailure(transaction);
				if (retrying) {
					return;
				}
				// Otherwise onFailure has just raised the shared error or warning modal, and this modal
				// has to get out of its way: the error modal renders in the legacy full-screen layer
				// inside the app root, while this one renders in a body portal at the same z-index, and
				// a body portal always comes later in the document. Staying open would paint this modal
				// over the error, so a failed save would look like nothing happened and the correlation
				// id needed to report it would never be seen. The shared error path cannot close this
				// modal itself the way it closes conductor-managed ones, because this modal is not in
				// that stack.
				//
				// Closing costs the operator the selection they had entered, which is the same trade the
				// shared warning path already makes for the editor that triggered it — the save was
				// refused either way, and an unreadable error is worse than a re-entered form. Both
				// state updates land in the same task, so the error modal is never painted behind this
				// one on its way out.
				onClose();
			}
		);
	};

	const saveCount = tab === SPECIFIC_TAB ? 0 : companiesToAdd.length;
	const saveLabel = saveCount === 0 ? 'Save' : `Save · ${saveCount}`;

	const specificTab = (
		<RolloutSpecificTab
			allUsers={allUsers}
			companyOptions={companyOptions}
			lockedCompanyName={editingRow?.companyName ?? null}
			onAddPerson={personId => setSelectedPersonIds(ids => (ids.includes(personId) ? ids : [...ids, personId]))}
			onAllUsersChange={setAllUsers}
			onRemovePerson={personId => setSelectedPersonIds(ids => ids.filter(id => id !== personId))}
			onSelectCompany={handleSelectCompany}
			personOptions={personOptions}
			selectedCompanyId={selectedCompanyId}
			selectedPersons={selectedPersons}
		/>
	);

	// EXTRA_LARGE is the one size the design system gives a fixed height to (setHeight applies a 700px
	// min-height). Reserving that height up front, together with the preview table's own scroll region
	// below, keeps the modal's overall size constant while a percentage target is dragged — the design
	// system centers a modal on its own box, so a modal that keeps changing height visibly shifts on
	// every company added or removed from the preview.
	return (
		<Modal closeOnInteractOutside={false} onCloseModal={onClose} setHeight size={Modal.SIZE.EXTRA_LARGE}>
			<Modal.Title>{detail.name}</Modal.Title>
			<Modal.Content>
				<Detail color="medium">
					{isEditing ? `Editing access for ${editingRow?.companyName}` : 'Add companies or users to this flag'}
				</Detail>
				{isEditing ? (
					specificTab
				) : (
					<Tabs onValueChange={setTab} value={tab}>
						<Tabs.List>
							<Tabs.Tab value={PERCENTAGE_TAB}>Percentage rollout</Tabs.Tab>
							<Tabs.Tab value={SPECIFIC_TAB}>Specific company or user</Tabs.Tab>
						</Tabs.List>
						<Tabs.Content value={PERCENTAGE_TAB}>
							<RolloutPercentageTab
								currentPercent={detail.rolloutPercent}
								currentPercentLabel={detail.rolloutPercentLabel}
								eligibleLabel={`${detail.eligibleGranted} of ${detail.eligibleTotal} eligible companies`}
								namedUsersOnlyCount={namedUsersOnlyEligibleCount}
								onTargetPercentChange={percent =>
									setTargetPercent(clampTargetPercent(percent, detail.rolloutPercent))
								}
								targetPercent={targetPercent}
							/>
							<RolloutPreviewTable
								candidates={candidates}
								emptyHint="Raise the target percentage to preview which companies get the flag."
								heldBackCompanyIds={heldBackCompanyIds}
								onToggleCompany={handleToggleCompany}
							/>
						</Tabs.Content>
						<Tabs.Content value={SPECIFIC_TAB}>{specificTab}</Tabs.Content>
					</Tabs>
				)}
			</Modal.Content>
			<Modal.Footer>
				{isEditing && editingCompanyId != null ? (
					<Modal.TertiaryFooterButton
						dataCy="rollout-remove-access"
						disabled={isSaving}
						onPress={() => commit(buildRemoveAccessInput(detail.key, editingCompanyId))}
					>
						Remove feature access
					</Modal.TertiaryFooterButton>
				) : null}
				<Modal.PrimaryFooterButton
					dataCy="rollout-save"
					disabled={pendingInput == null || isSaving}
					onPress={() => pendingInput != null && commit(pendingInput)}
				>
					{saveLabel}
				</Modal.PrimaryFooterButton>
				{/* Disabled while saving so Cancel cannot suggest an escape that is not real: the mutation
				    already in flight is not aborted by closing this modal, and completes regardless.
				    The design system's Modal does not expose a way to also block the Radix Escape-key
				    close path here, so that path is not gated — a known, narrower residual gap. */}
				<Modal.SecondaryFooterButton dataCy="rollout-cancel" disabled={isSaving} onPress={onClose}>
					Cancel
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
}
